import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import {
  CobaltFormField,
  CobaltFormFieldLabel,
} from '@cae/cobalt-react/components/form-field';
import { CobaltModal } from '@cae/cobalt-react/components/modal';
import { CobaltTextInput } from '@cae/cobalt-react/components/text-input';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { DISCIPLINE_DISPLAY_NAME_MAP } from '@features/instructors/utils/discipline.utils';
import * as React from 'react';

import type { InstructorRevokeModalProps } from './InstructorRevokeModal.types';

import styles from './InstructorRevokeModal.module.css';
import { MAX_LENGTH } from './InstructorRevokeModal.utils';

const InstructorRevokeModal = React.memo(
  ({
    isOpen,
    onClose,
    onConfirm,
    disciplineName,
    primaryTrainingCenterName,
    programId,
    instructorDisplayName,
  }: Readonly<InstructorRevokeModalProps>): React.ReactElement => {
    const [description, setDescription] = React.useState<string>('');
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.value.length <= MAX_LENGTH) {
          setDescription(e.target.value);
        }
      },
      []
    );

    const handleConfirm = React.useCallback(() => {
      onConfirm(description);
    }, [onConfirm, description]);

    React.useEffect(() => {
      if (!isOpen) setDescription('');
    }, [isOpen]);

    return (
      <CobaltModal
        isOpen={isOpen}
        size={isMobileOrTablet ? 'small' : 'medium'}
        title="Revocation request"
        showCloseButton={true}
        onClose={onClose}
        actionBar={
          <CobaltActionBar
            position="right"
            primaryButton={
              <CobaltButton variant="primary-danger" onClick={handleConfirm}>
                Revoke
              </CobaltButton>
            }
            secondaryButton={
              <CobaltButton variant="secondary" onClick={onClose}>
                Cancel
              </CobaltButton>
            }
          />
        }
      >
        <div
          className={styles.modalContainer}
          data-testid="instructor-revoke-modal"
        >
          <div className={styles.revokeDetailsContainer}>
            <div className={styles.revokeDetailsSection}>
              <p className={`body-01 ${styles.revokeDetailsSectionHeading}`}>
                Program
              </p>
              <p className={`body-02 ${styles.revokeDetailsSectionText}`}>
                {programId}
              </p>
            </div>
            <div className={styles.revokeDetailsSection}>
              <p className={`body-01 ${styles.revokeDetailsSectionHeading}`}>
                Location
              </p>
              <p className={`body-02 ${styles.revokeDetailsSectionText}`}>
                {primaryTrainingCenterName}
              </p>
            </div>
            <div className={styles.revokeDetailsSection}>
              <p className={`body-01 ${styles.revokeDetailsSectionHeading}`}>
                Instructor type
              </p>
              <p className={`body-02 ${styles.revokeDetailsSectionText}`}>
                {DISCIPLINE_DISPLAY_NAME_MAP[disciplineName]}
              </p>
            </div>
          </div>
          <p className={`heading-01 ${styles.revokeReasonPrompt}`}>
            Tell us why you are revoking {instructorDisplayName}
          </p>
          <div className={styles.descriptionInput}>
            <CobaltFormField id="revokeReason">
              <CobaltFormFieldLabel
                htmlFor="revokeReason"
                label="Description and details (optional)"
                labelAccessory={`${description.length}/${MAX_LENGTH}`}
              />
              <CobaltTextInput
                value={description}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </CobaltFormField>
          </div>
        </div>
      </CobaltModal>
    );
  }
);

InstructorRevokeModal.displayName = 'InstructorRevokeModal';
export { InstructorRevokeModal };
